import { Component, Inject, Input, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { OKTA_AUTH } from '@okta/okta-angular';
import OktaAuth from '@okta/okta-auth-js';
import { InitUser, SetUserSignInFlag } from 'src/app/core/store/user/user.actions';
import { asFormControl } from 'src/app/core/utils/common';
import { passwordMatchValidator, passwordValidator } from 'src/app/core/utils/validators';

@Component({
  selector: 'app-update-form',
  templateUrl: './update-form.component.html',
  styleUrls: ['./update-form.component.scss'],
})
export class UpdateFormComponent implements OnInit {
  @Input() recoveryToken!: string;
  @Input() onSignIn!: () => void;

  updateForm = new FormGroup(
    {
      newPassword: new FormControl('', [Validators.required, passwordValidator()]),
      confirmPassword: new FormControl('', [Validators.required]),
    },
    [passwordMatchValidator('newPassword', 'confirmPassword')]
  );

  disableButton: boolean = true;
  errorMessage: string = '';

  asFormControl = asFormControl;

  constructor(@Inject(OKTA_AUTH) private oktaAuth: OktaAuth, private store: Store) {}

  async ngOnInit(): Promise<void> {
    try {
      const response = await this.oktaAuth.verifyRecoveryToken({
        recoveryToken: this.recoveryToken,
      });

      if (response.status !== 'PASSWORD_RESET') {
        throw new Error('Invalid recovery token.');
      }

      this.disableButton = false;
    } catch (err: any) {
      this.errorMessage = err.message;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.updateForm.invalid) {
      this.updateForm.markAllAsTouched();
      return;
    }

    this.disableButton = true;
    this.errorMessage = '';

    try {
      const response = await this.oktaAuth.tx.introspect();

      const transaction = await response.resetPassword!({
        newPassword: this.updateForm.value.newPassword,
      });

      if (transaction.status === 'SUCCESS') {
        const authenticateResponse = await this.oktaAuth.idx.authenticate({
          username: response.user?.['profile'].login,
          password: this.updateForm.value.newPassword,
        });

        if (authenticateResponse.status === 'SUCCESS') {
          const { tokens } = authenticateResponse;

          if (!tokens) {
            throw new Error('Something went wrong. Please try again.');
          }

          this.store.dispatch(new SetUserSignInFlag(true)).subscribe(() => {
            setTimeout(async () => {
              await this.oktaAuth.tokenManager.setTokens(tokens);

              const url = new URL(window.location.href);
              url.search = '';
              window.history.replaceState({}, document.title, url.toString());

              this.store.dispatch(new InitUser());
            }, 0);
          });
        } else {
          throw new Error('Something went wrong. Please try again.');
        }
      } else {
        throw new Error('Something went wrong. Please try again.');
      }
    } catch (err: any) {
      if (err.errorCode === 'E0000080') {
        this.errorMessage = 'Password must be different from the previous 24 passwords.';
      } else {
        this.errorMessage = err.message;
      }

      this.disableButton = false;
    }
  }
}
